import type { Session } from '../../lib/types';
import { localMinutesOfDay } from '../../lib/time';

// Shared geometry + overlap layout for the resource (day) and week timelines.
export const SLOT_PX = 56; // height of one slot row
export const HEADER_PX = 44;

export interface Laid {
  session: Session;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/**
 * Greedy interval-graph layout: cluster overlapping sessions, pack each cluster
 * into side-by-side lanes. Used per-column (per employee in Day view, per day in
 * Week view).
 */
export function layout(sessions: Session[], dayStart: number, pxPerMin: number): Laid[] {
  const items = sessions
    .map((s) => ({ s, start: localMinutesOfDay(s.start_at), end: localMinutesOfDay(s.end_at) }))
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const out: Laid[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -Infinity;

  const flush = (): void => {
    if (cluster.length === 0) return;
    const laneEnds: number[] = [];
    const laneOf = new Map<number, number>();
    cluster.forEach((it, idx) => {
      let lane = laneEnds.findIndex((e) => e <= it.start);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(it.end); } else laneEnds[lane] = it.end;
      laneOf.set(idx, lane);
    });
    const lanes = laneEnds.length;
    cluster.forEach((it, idx) => {
      out.push({
        session: it.s,
        top: (it.start - dayStart) * pxPerMin,
        height: Math.max((it.end - it.start) * pxPerMin - 3, 20),
        lane: laneOf.get(idx) ?? 0,
        lanes,
      });
    });
    cluster = [];
  };

  for (const it of items) {
    if (it.start >= clusterEnd && cluster.length) flush();
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  flush();
  return out;
}
